import { configureStore } from '@reduxjs/toolkit';
import { sessionCache } from './sessionCache';

// Seed the impersonation token into initial Redux state from localStorage.
// The axios interceptor attaches the Bearer by reading this slice, and
// child page components (home, customer-360, etc.) mount during the app
// component's *initial* render — before onMounted runs — so any
// tools.getUserContext() they fire would otherwise race the init and
// miss the Bearer. Seeding here means the token is in place on the very
// first request. initImpersonation() still runs the validation round-trip
// shortly after and either promotes this to full session state (slice,
// reason, expiresAt, customer name) or clears it if the backend no longer
// recognizes the session.
function getInitialImpersonationState() {
    try {
        const authToken = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        return authToken ? { token: authToken } : null;
    } catch {
        return null;
    }
}

// METALARK-835: the pending-invitations cache survives page reloads
// within a tab via sessionStorage. Without this, the navbar badge
// would reset on every reload until the user re-visits /invitations
// or /accept-invitation, which feels broken for what is effectively
// a notification surface.
//
// Storage is tab-scoped (sessionStorage, not localStorage) — closing
// the tab clears it; a parallel tab gets its own copy. The payload
// carries the cached `userUuid` so a different user signing in on
// the same tab (without an explicit logout) won't see stale state:
// userService.listMyInvitations verifies the userUuid matches the
// current session and forces a refetch on mismatch.
const PENDING_INVITES_STORAGE_KEY = 'cloudcore.pendingInvitations';

function getInitialPendingInvitationsState() {
    try {
        if (typeof sessionStorage === 'undefined') return null;
        const raw = sessionStorage.getItem(PENDING_INVITES_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Validate shape — anything malformed is safer to drop than
        // to feed through. A fresh fetch will rebuild the cache on
        // first /invitations or /accept-invitation visit.
        if (!parsed || !Array.isArray(parsed.invitations)) return null;
        return parsed;
    } catch {
        return null;
    }
}

const initialState = {
    requests: 0,
    loadingMessage: "",
    sliceId: "",
    jobs: {},
    resources: {},
    impersonation: getInitialImpersonationState(),
    userContext: null, // User auth context from /auth/me
    environment: null, // Environment config with internal_sso, etc.
    customer: null, // Full customer object { customerId, name, logoUrl, etc. }
    quotaDetails: null, // Quota info: { totalCredits, usedCredits, remainingCredits }
    subscriptionStatus: null, // /billing/subscription payload — shared by subscription-banner and upload page
    cacheBuster: Date.now(), // Cache buster for forcing hard refresh of images and other assets
    activeRequests: {}, // Map of requestId -> { method, startTime, timerPendingId, visible }
    isBlockingOverlay: false, // True if any POST/PUT/DELETE request is visible
    subscriptionError: null, // 402 error details for subscription modal
    // METALARK-835: pending-invitation cache. Lazily populated on first
    // /invitations/mine fetch (from either the invitations page or the
    // accept-invitation page's stale-link fallback) and shared between
    // them so navigating between the two surfaces doesn't double-fetch.
    // Shape: { invitations: [{roleAssignmentId, invitationToken, customerName, ...}], loadedAt: ms }.
    // null means "never fetched"; an empty array means "fetched, none pending".
    pendingInvitations: getInitialPendingInvitationsState()
};

function appReducer(state = initialState, action) {
    switch (action.type) {
        case 'setSliceId':
            return {...state,
                sliceId: action.payload
            };
        case 'setJobs':
            return {...state,
                jobs: action.payload
            };
        case 'setResources':
            return {...state,
                resources: action.payload
            };
        case 'incrementRequests':
            return {...state,
                requests: state.requests + 1
            };
        case 'decrementRequests':
            return {...state,
                requests: Math.max(0, state.requests - 1)
            };
        case 'setLoadingMessage':
            return {...state,
                loadingMessage: action.payload
            };
        case 'clearLoadingMessage':
            return {...state,
                loadingMessage: ""
            };
        case 'setImpersonation':
            return {...state,
                impersonation: action.payload
            };
        case 'clearImpersonation':
            return {...state,
                impersonation: null
            };
        case 'setUserContext':
            return {...state,
                userContext: action.payload
            };
        case 'clearUserContext':
            return {...state,
                userContext: null
            };
        case 'setEnvironment':
            return {...state,
                environment: action.payload
            };
        case 'SET_CUSTOMER':
            return {...state,
                customer: action.payload
            };
        case 'CLEAR_CUSTOMER':
            return {...state,
                customer: null
            };
        case 'setQuotaDetails':
            return {...state,
                quotaDetails: action.payload
            };
        case 'setSubscriptionStatus':
            return {...state,
                subscriptionStatus: action.payload
            };
        case 'refreshCacheBuster':
            return {...state,
                cacheBuster: Date.now()
            };
        case 'addRequest':
            // Track a new request with method and timestamp
            return {...state,
                activeRequests: {
                    ...state.activeRequests,
                    [action.payload.requestId]: {
                        method: action.payload.method,
                        url: action.payload.url,
                        startTime: action.payload.startTime,
                        timerPendingId: null,
                        visible: false
                    }
                }
            };
        case 'removeRequest':
            // Clean up a completed request
            const { [action.payload]: _, ...remainingRequests } = state.activeRequests;
            return {...state,
                activeRequests: remainingRequests,
                isBlockingOverlay: Object.values(remainingRequests).some(
                    req => req.visible && ['POST', 'PUT', 'DELETE'].includes(req.method)
                )
            };
        case 'setVisibleRequest':
            // Mark request as visible (passed delay threshold)
            return {...state,
                activeRequests: {
                    ...state.activeRequests,
                    [action.payload]: {
                        ...state.activeRequests[action.payload],
                        visible: true
                    }
                },
                isBlockingOverlay: Object.values({
                    ...state.activeRequests,
                    [action.payload]: {
                        ...state.activeRequests[action.payload],
                        visible: true
                    }
                }).some(req => req.visible && ['POST', 'PUT', 'DELETE'].includes(req.method))
            };
        case 'setRequestTimer':
            // Store timer ID for cleanup on early request completion
            return {...state,
                activeRequests: {
                    ...state.activeRequests,
                    [action.payload.requestId]: {
                        ...state.activeRequests[action.payload.requestId],
                        timerPendingId: action.payload.timerId
                    }
                }
            };
        case 'showSubscriptionError':
            // Store 402 error details for subscription modal
            return {...state,
                subscriptionError: action.payload
            };
        case 'clearSubscriptionError':
            // Clear subscription error after modal is dismissed
            return {...state,
                subscriptionError: null
            };
        case 'setPendingInvitations': {
            // METALARK-835: cache the full pending-invitations list so
            // /accept-invitation's stale-link fallback and /invitations
            // share one fetch per session. Payload may be either a
            // bare array (legacy) or `{ userUuid, invitations }`; the
            // userUuid form is preferred because it lets us detect
            // cross-user cache poisoning on the same tab (sign-out
            // without explicit logout, then sign in as someone else).
            const payload = action.payload || {};
            const invitations = Array.isArray(payload)
                ? payload
                : (Array.isArray(payload.invitations) ? payload.invitations : []);
            const userUuid = Array.isArray(payload) ? null : (payload.userUuid || null);
            const next = {
                userUuid,
                invitations,
                loadedAt: Date.now(),
            };
            try {
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.setItem(PENDING_INVITES_STORAGE_KEY, JSON.stringify(next));
                }
            } catch (_) {}
            return { ...state, pendingInvitations: next };
        }
        case 'removePendingInvitation': {
            // Drop a single row after the user accepts or declines it
            // so subsequent reads of the cache reflect reality without
            // a network round-trip. Payload: roleAssignmentId.
            if (!state.pendingInvitations) return state;
            const next = {
                ...state.pendingInvitations,
                invitations: state.pendingInvitations.invitations.filter(
                    i => i.roleAssignmentId !== action.payload
                ),
            };
            try {
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.setItem(PENDING_INVITES_STORAGE_KEY, JSON.stringify(next));
                }
            } catch (_) {}
            return { ...state, pendingInvitations: next };
        }
        case 'clearPendingInvitations':
            // Force-invalidate the cache (e.g. on logout). The next
            // listMyInvitations call will refetch from the backend.
            try {
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.removeItem(PENDING_INVITES_STORAGE_KEY);
                }
            } catch (_) {}
            return { ...state, pendingInvitations: null };
        default:
            return state;
    }
}

export const store = configureStore({ reducer: appReducer });

window.store = store;